// src/pages/Pay.tsx (통째로 교체용 최종본)
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../lib/auth";
import { displayServiceName } from "../lib/serviceLabel";

type PaymentStatus = "requested" | "approved" | "complete" | "failed" | "rejected";

type Payment = {
  id: number;
  order_id: string;
  title: string;

  /** ✅ 원금 */
  amount: number;

  /** ✅ 분할결제 정보 */
  group_id?: number | null;
  split_index?: number | null;
  split_count?: number | null;
  base_order_id?: string | null;

  /** ✅ DB 표준(권장): fee_amount=수수료만, vat_amount=부가세, total_amount=총액 */
  fee_amount?: number | null;
  vat_amount?: number | null;
  total_amount?: number | null;

  /** ✅ 과거/호환 필드(있으면 대응) */
  totalAmount?: number | null;
  total?: number | null;
  pay_amount?: number | null;
  fee?: number | null;
  vat?: number | null;

  /** ✅ 서버에서 내려주는 "토큰 포함 결제 URL" (있으면 우선 사용) */
  pay_url_with_token?: string | null;

  status: PaymentStatus;
  approval_status?: string | null;
  reject_reason?: string | null;
  meta?: any;

  /** ✅ 화면에 표시하고 싶은 필드들(서버가 내려주면 사용) */
  category?: string | null;
  counterparty_name?: string | null;
  account_holder?: string | null;
  receiver_name?: string | null;
  depositor_name?: string | null;

  created_at: string;
};

type TabKey = "all" | "requested" | "approved" | "complete" | "failed";

const TAB_LABEL: Record<TabKey, string> = {
  all: "전체",
  requested: "대기",
  approved: "결제 가능",
  complete: "완료",
  failed: "요청철회/거절",
};

function statusMeta(status?: string | null) {
  const s = String(status || "").toLowerCase();

  switch (s) {
    case "complete":
    case "completed":
      return { label: "완료", cls: "bg-emerald-50 text-emerald-700 ring-emerald-100" };
    case "approved":
      return { label: "결제 가능", cls: "bg-sky-50 text-sky-700 ring-sky-100" };
    case "rejected":
    case "failed":
      return { label: "요청철회/거절", cls: "bg-rose-50 text-rose-700 ring-rose-100" };
    case "requested":
    case "pending":
    default:
      return { label: "대기", cls: "bg-amber-50 text-amber-700 ring-amber-100" };
  }
}

function toNum(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function getAmountBreakdown(p: Payment) {
  const principal = toNum(p.amount) ?? 0;

  const total =
    toNum(p.total_amount) ??
    toNum(p.totalAmount) ??
    toNum(p.total) ??
    toNum(p.pay_amount);

  const feeOnlyCandidate = toNum(p.fee_amount) ?? toNum(p.fee);
  const vatCandidate = toNum(p.vat_amount) ?? toNum(p.vat);

  if (total !== null) {
    const diff = Math.max(0, total - principal);

    let feeForDisplay: number | null = null;
    let vatForDisplay: number | null = null;

    if (feeOnlyCandidate !== null && vatCandidate !== null) {
      if (Math.abs(feeOnlyCandidate + vatCandidate - diff) <= 1) {
        feeForDisplay = feeOnlyCandidate;
        vatForDisplay = vatCandidate;
      } else if (Math.abs(feeOnlyCandidate - diff) <= 1) {
        vatForDisplay = vatCandidate;
        feeForDisplay = Math.max(0, diff - vatForDisplay);
      } else {
        feeForDisplay = feeOnlyCandidate;
        vatForDisplay = vatCandidate;
      }
    } else if (vatCandidate !== null) {
      vatForDisplay = vatCandidate;
      feeForDisplay = Math.max(0, diff - vatForDisplay);
    } else if (feeOnlyCandidate !== null) {
      feeForDisplay = feeOnlyCandidate;
      vatForDisplay = null;
    }

    return {
      principal,
      fee: feeForDisplay,
      vat: vatForDisplay,
      total,
      hasTotal: true,
      hasBreakdown: feeForDisplay !== null || vatForDisplay !== null,
    };
  }

  if (feeOnlyCandidate !== null || vatCandidate !== null) {
    const t = principal + (feeOnlyCandidate ?? 0) + (vatCandidate ?? 0);
    return {
      principal,
      fee: feeOnlyCandidate,
      vat: vatCandidate,
      total: t,
      hasTotal: true,
      hasBreakdown: true,
    };
  }

  return {
    principal,
    fee: null,
    vat: null,
    total: principal,
    hasTotal: false,
    hasBreakdown: false,
  };
}

function shouldHidePayment(p: Payment) {
  const title = String(p.title || "").replace(/\s/g, "");
  const catRaw = String(p.category || "").replace(/\s/g, "");
  const cat = catRaw.toLowerCase();

  if (title.includes("등기부등본") || title.includes("등기부")) return true;
  if (cat.includes("등기부") || cat.includes("registry")) return true;

  const maybeTotal =
    toNum(p.total_amount) ??
    toNum(p.total) ??
    toNum(p.totalAmount) ??
    toNum(p.pay_amount) ??
    0;

  if ((title.includes("발급") || title.includes("수수료")) && maybeTotal === 1200) {
    return true;
  }

  return false;
}

function pickCounterpartyName(p: Payment): string | null {
  const v =
    p.counterparty_name ||
    p.account_holder ||
    p.receiver_name ||
    p.depositor_name ||
    null;
  return v ? String(v).trim() : null;
}

function hasVatNoticeAgreed(p: Payment): boolean {
  const meta = p.meta || {};
  return !!(meta.vat_notice_agreed || meta.vat_notice_agreed_at);
}

export default function Pay() {
  const navigate = useNavigate();

  const [items, setItems] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabKey>("all");
  const [payingId, setPayingId] = useState<number | null>(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentTarget, setConsentTarget] = useState<Payment | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ✅ 신한카드 안내 배너 ON/OFF (필요 시 나중에 env로 분리 가능)
  const SHINHAN_NOTICE = true;

  const loadPayments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch("/api/payments/my");
      const j = await res.json();
      if (!res.ok || !j.ok) {
        throw new Error(j?.message || "결제요청 목록을 불러오지 못했습니다.");
      }
      setItems(j.items || []);
    } catch (e: any) {
      console.error("[Pay] load error", e);
      setError(e?.message || "목록 로딩 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadPayments();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [loadPayments]);

  useEffect(() => {
    const handler = () => {
      loadPayments().catch(() => void 0);
    };
    window.addEventListener("focus", handler);
    return () => {
      window.removeEventListener("focus", handler);
    };
  }, [loadPayments]);

  const visibleItems = useMemo(
    () => items.filter((p) => !shouldHidePayment(p)),
    [items]
  );

  const filtered = useMemo(() => {
    if (tab === "all") return visibleItems;
    if (tab === "failed") {
      return visibleItems.filter(
        (p) => p.status === "failed" || p.status === "rejected"
      );
    }
    return visibleItems.filter((p) => p.status === tab);
  }, [visibleItems, tab]);

  const emptyMessage = useMemo(() => {
    if (loading) return "";
    if (tab === "approved") return "결제 가능한 요청이 없습니다.";
    if (tab === "requested") return "대기 중인 결제요청이 없습니다.";
    if (tab === "complete") return "완료된 결제내역이 없습니다.";
    if (tab === "failed") return "요청철회 또는 거절된 결제내역이 없습니다.";
    return "등록된 결제요청이 없습니다.";
  }, [tab, loading]);

  const fmtAmount = (n: number) =>
    n.toLocaleString("ko-KR", { maximumFractionDigits: 0 }) + "원";

  const fmtDateTime = (iso: string) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("ko-KR");
    } catch {
      return iso;
    }
  };

  const doPay = (p: Payment) => {
    if (payingId) return;
    setPayingId(p.id);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("로그인이 필요합니다.");
        setPayingId(null);
        return;
      }

      const fallbackUrl = `/api/payments/${p.id}/pay-form?token=${encodeURIComponent(token)}`;
      let url = p.pay_url_with_token || fallbackUrl;

      try {
        const u = new URL(url, window.location.origin);
        const host = u.host || "";
        if (host && host !== window.location.host) {
          url = fallbackUrl;
        }
      } catch {
        url = fallbackUrl;
      }

      window.location.href = url;
    } catch (e) {
      console.error("[Pay] handlePay error", e);
      alert("결제를 시작하는 중 오류가 발생했습니다.");
      setPayingId(null);
    }
  };

  const handlePay = (p: Payment) => {
    if (payingId) return;
    if (hasVatNoticeAgreed(p)) {
      doPay(p);
      return;
    }
    setConsentTarget(p);
    setConsentChecked(false);
    setConsentOpen(true);
  };

  const handleConsentConfirm = async () => {
    if (!consentTarget) return;
    if (!consentChecked) {
      alert("동의가 필요합니다.");
      return;
    }

    setConsentSaving(true);
    try {
      const res = await authFetch(`/api/payments/${consentTarget.id}/vat-notice-agree`, {
        method: "POST",
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j?.ok) throw new Error(j?.message || "동의 저장 실패");

      setConsentOpen(false);
      setConsentTarget(null);
      setConsentChecked(false);
      doPay(consentTarget);
    } catch (e: any) {
      alert(e?.message || "동의 저장 중 오류가 발생했습니다.");
    } finally {
      setConsentSaving(false);
    }
  };

  const goEdit = (id: number) => {
    navigate(`/pay/${id}/edit`);
  };

  return (
    <div className="min-h-[100dvh] bg-slate-50 pb-20">
      <div className="mx-auto max-w-5xl px-4">
        <div className="pt-2 sm:pt-3">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            결제하기
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            관리자 승인 후 "결제 가능" 상태에서 결제를 진행할 수 있습니다.
          </p>

          {SHINHAN_NOTICE && (
            <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-sm">
              <div className="font-extrabold">신한카드 결제 제한 안내</div>
              <div className="mt-1 leading-relaxed text-amber-900/90">
                현재 PG 정책으로 <span className="font-semibold">신한카드 결제가 지원되지 않습니다.</span>{" "}
                다른 카드로 결제해 주세요.
              </div>
            </div>
          )}
        </div>

        {/* ✅ 탭 */}
        <div className="mt-5">
          <div className="rounded-full bg-slate-100 p-1.5">
            <div
              className={[
                "flex gap-1 whitespace-nowrap",
                "overflow-x-auto",
                "px-0.5 py-0.5",
                "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
                "[WebkitOverflowScrolling:touch]",
              ].join(" ")}
            >
              {(Object.keys(TAB_LABEL) as TabKey[]).map((k) => {
                const active = tab === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setTab(k)}
                    className={[
                      "shrink-0 rounded-full",
                      "px-3 py-2 sm:px-4",
                      "text-[11px] sm:text-sm font-semibold leading-none",
                      "transition",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50",
                      active
                        ? "bg-sky-600 text-white shadow-sm"
                        : "text-slate-600 hover:text-sky-700",
                    ].join(" ")}
                  >
                    {TAB_LABEL[k]}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        <div className="mt-5 space-y-4">
          {loading && (
            <div className="rounded-2xl bg-white p-6 text-sm text-slate-500 shadow-sm ring-1 ring-slate-200/70">
              불러오는 중입니다...
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="rounded-2xl bg-white p-6 text-sm text-slate-500 shadow-sm ring-1 ring-slate-200/70">
              {emptyMessage}
            </div>
          )}

          {filtered.map((p) => {
            const isApproved = p.status === "approved";
            const isRequested = p.status === "requested";
            const isFailedOrRejected = p.status === "failed" || p.status === "rejected";
            const canEdit = isRequested || isFailedOrRejected;

            const meta = statusMeta(p.status);
            const b = getAmountBreakdown(p);

            const cat = String(p.category || "").trim();
            const counterparty = pickCounterpartyName(p);

            const displayCat = displayServiceName(cat);
            const displayCounterparty = counterparty;

            const primaryTitle =
              displayCat || displayCounterparty
                ? [displayCat || null, displayCounterparty || null].filter(Boolean).join(", ")
                : displayServiceName(p.title) || "이체 결제";

            const rawSecondary =
              p.title && primaryTitle !== displayServiceName(p.title) ? p.title : null;
            const secondaryTitle = rawSecondary ? displayServiceName(rawSecondary) : null;

            return (
              <div
                key={p.id}
                className="flex flex-col gap-3 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/70 md:flex-row md:items-center md:justify-between"
              >
                <div className="flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">
                      {primaryTitle}
                    </span>

                    {p.split_count && p.split_index && (
                      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 bg-amber-50 text-amber-700 ring-amber-200">
                        분할 {p.split_index}/{p.split_count}
                      </span>
                    )}

                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${meta.cls}`}
                    >
                      {meta.label}
                    </span>
                  </div>

                  {secondaryTitle && (
                    <div className="text-xs text-slate-500">
                      {secondaryTitle}
                    </div>
                  )}

                  <div className="text-xs text-slate-500">
                    신청일: {fmtDateTime(p.created_at)}
                  </div>

                  {p.approval_status && (
                    <div className="text-xs text-slate-400">
                      승인상태: {p.approval_status}
                    </div>
                  )}

                  {p.reject_reason && (
                    <div className="text-xs text-rose-500">
                      거절 사유: {p.reject_reason}
                    </div>
                  )}
                </div>

                <div className="flex flex-col items-end gap-1 md:min-w-[220px]">
                  <div className="text-right text-lg font-semibold text-sky-700">
                    {fmtAmount(b.total)}
                  </div>

                  {b.hasBreakdown && (
                    <div className="text-right text-[11px] text-slate-500">
                      <span>원금 {fmtAmount(b.principal)}</span>
                      {b.fee !== null && <span> · 수수료 {fmtAmount(b.fee)}</span>}
                      {b.vat !== null && <span> · 부가세 {fmtAmount(b.vat)}</span>}
                    </div>
                  )}

                  <div className="mt-1 flex gap-2">
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => goEdit(p.id)}
                        className="inline-flex items-center justify-center rounded-full border border-slate-300 px-4 py-2 text-xs sm:text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        수정하기
                      </button>
                    )}

                    <button
                      type="button"
                      disabled={!isApproved || payingId === p.id}
                      onClick={() => handlePay(p)}
                      className={[
                        "inline-flex items-center justify-center rounded-full px-5 py-2 text-xs sm:text-sm font-semibold shadow-sm transition",
                        isApproved
                          ? "bg-sky-600 text-white hover:bg-sky-700"
                          : "bg-slate-200 text-slate-500 cursor-not-allowed",
                      ].join(" ")}
                    >
                      {payingId === p.id ? "진행 중..." : "결제하기"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {consentOpen && consentTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="text-lg font-bold text-slate-900">부가세 중복 환급 불가 안내</div>
            <p className="mt-3 text-sm leading-relaxed text-slate-700">
              부가세 처리는 거래당사자가 발급한 세금계산서/현금영수증 기준으로 진행해 주세요.
              당사 카드결제건을 통해 추가 부가세 환급을 시도할 경우 세법위반이 발생할 수 있습니다.
            </p>

            <label className="mt-4 flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
              />
              위 내용을 확인했으며 동의합니다.
            </label>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-full px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                onClick={() => {
                  if (consentSaving) return;
                  setConsentOpen(false);
                  setConsentTarget(null);
                  setConsentChecked(false);
                }}
              >
                취소
              </button>
              <button
                type="button"
                disabled={!consentChecked || consentSaving}
                onClick={handleConsentConfirm}
                className={[
                  "rounded-full px-5 py-2 text-sm font-semibold text-white",
                  consentChecked && !consentSaving
                    ? "bg-sky-600 hover:bg-sky-700"
                    : "bg-slate-300 cursor-not-allowed",
                ].join(" ")}
              >
                {consentSaving ? "처리 중..." : "동의하기"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
